// Copyright (C) 2021 Bystronic Laser AG

import {
  AlertOperator,
  AlertSeverity,
  CareAlertMetric,
} from './enums/AlertsEnums';
import { CareAlertType } from './enums/CareAlertType';
import { FilterTimeAxisSpanEnum } from './enums/FilterTimeAxisSpanEnum';
import { Device } from '@/models/device';

/**
 * Alert used in the Care console (just with fields needed for the list).
 *
 * Represents Custom and Preset alerts. Custom alerts are created directly on a
 * device to be used as personalized alerts to fulfil user needs. Preset alerts
 * are created on a device from a standard alert definition.
 */
export interface CareAlertListItem {
  id: number;
  type: CareAlertType;
  status: CareAlertStatus;
  customerName: string;
  subsidiaryName: string;
  device: CareAlertDevice;
  severity: AlertSeverity;
  name: string;
  // KPI in the UI
  metric: CareAlertMetric;
  // Time aggregation in the UI
  dateGrouping: FilterTimeAxisSpanEnum;

  // These aren't shown in the table but are matched by filters:

  operator: AlertOperator;
  threshold: number;
  eventSources: string[];
  eventCodes: string[];
  variable: string | undefined;
}

/**
 * Alert created directly on a device to be used as personalized alert to fulfil
 * user needs.
 */
export interface CustomAlertListItem extends CareAlertListItem {}

/**
 * Alert created on a device from a standard alert definition.
 */
export interface PresetAlertListItem extends CareAlertListItem {
  /** Id of the standard alert from which it has been created. */
  // Standard alert number in the UI
  standardAlertId: number;
}

/**
 * Alert used in the Care console (with additional fields needed for the detail).
 */
export interface CareAlert extends CareAlertListItem {
  // Detail's 1st section

  description: string | null;
  createdBy: string;
  deactivationDate: Date | null;
  deactivatedBy: string | null;

  // Detail's 2nd section

  operator: AlertOperator;
  eventSources: string[];
  eventCodes: string[];
  variable: string | undefined;
}

/**
 * Alert created directly on a device to be used as personalized alert to fulfil
 * user needs (with additional fields needed for the detail).
 */
export interface CustomAlert extends CareAlert {}

/**
 * Alert created on a device from a standard alert definition (with additional
 * fields needed for the detail).
 */
export interface PresetAlert extends CareAlert {
  /** Id of the standard alert from which it has been created. */
  // Standard alert number in the UI
  standardAlertId: number;
}

/** Information about the device the Care alert is applied to. */
export interface CareAlertDevice {
  name: Device['name'];
  equipmentId: Device['equipmentId'];
  model: Device['deviceBaseModelName'];
}

export enum CareAlertStatus {
  /** The alert is applied to some device but not triggered. */
  Active = 1,

  /** The alert has been triggered. */
  InUse = 2,

  /** No longer detecting (soft deletion). */
  Inactive = 3,
}

export const nullCareAlert: CareAlert = Object.freeze({
  id: 0,
  type: CareAlertType.Custom,
  status: CareAlertStatus.Inactive,
  customerName: '',
  subsidiaryName: '',
  device: {
    name: '',
    equipmentId: '',
    model: '',
  },
  name: '',
  metric: CareAlertMetric.DrawerChanges,
  dateGrouping: FilterTimeAxisSpanEnum.Day,

  operator: AlertOperator.Equal,
  threshold: 0,
  eventSources: [],
  eventCodes: [],
  variable: undefined,

  description: '',
  severity: AlertSeverity.Low,
  createdBy: '',
  deactivationDate: null,
  deactivatedBy: '',
});
